import { type FC, type ReactNode } from "react";
import { View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import type { PaginationNavDirection, PaginationProps } from "./Pagination.types";
import { CONTROL_SIZE } from "./pagination-metrics";
import { PaginationNavButton, PaginationPageButton, PaginationSummary } from "./parts";
import { usePagination } from "./usePagination";

const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];

/** Only reached when a product passes an empty `pageSizeOptions`. */
const FALLBACK_PAGE_SIZE = 10;

const Pagination: FC<PaginationProps> = ({
  totalItems,
  page: controlledPage,
  defaultPage,
  onPageChange,
  pageSize: controlledPageSize,
  defaultPageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  siblingCount = 1,
  boundaryCount = 1,
  showSummary = true,
  showPageSizeSelect = true,
  pageSizeMenuPlacement = "top",
  variant = "plain",
  loading = false,
  disabled = false,
  summaryLabel = "Showing",
  formatTotal = defaultFormatTotal,
  previousLabel = "Previous page",
  nextLabel = "Next page",
  pageSizeLabel = "Items per page",
  formatPageLabel = defaultFormatPageLabel,
  accessibilityLabel = "Pagination",
  renderSummary,
  renderPageButton,
  renderNavButton,
  style,
  testID,
}) => {
  const {
    page,
    pageSize,
    pageCount,
    rangeStart,
    rangeEnd,
    canGoPrevious,
    canGoNext,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    changePageSize,
    items,
  } = usePagination({
    totalItems,
    page: controlledPage,
    defaultPage,
    onPageChange,
    pageSize: controlledPageSize,
    // An uncontrolled control starts on the first size it offers, so a product
    // that changes `pageSizeOptions` does not also have to restate the default.
    defaultPageSize: defaultPageSize ?? pageSizeOptions[0] ?? FALLBACK_PAGE_SIZE,
    onPageSizeChange,
    siblingCount,
    boundaryCount,
  });

  const rootStyle = [styles.root, variant === "bar" && styles.rootBar, style];

  if (loading) {
    return (
      <View style={rootStyle} testID={testID} accessibilityLabel={accessibilityLabel} aria-busy>
        <Skeleton variant="rounded" width={168} height={CONTROL_SIZE} />
        <Skeleton variant="rounded" width={196} height={CONTROL_SIZE} />
      </View>
    );
  }

  const summaryElement =
    showSummary ?
      <PaginationSummary
        label={summaryLabel}
        totalText={formatTotal(totalItems)}
        pageSize={pageSize}
        pageItemCount={totalItems === 0 ? 0 : rangeEnd - rangeStart + 1}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={changePageSize}
        showPageSizeSelect={showPageSizeSelect && pageSizeOptions.length > 1}
        pageSizeLabel={pageSizeLabel}
        disabled={disabled}
        menuPlacement={pageSizeMenuPlacement}
      />
    : null;

  const summary =
    renderSummary === undefined || summaryElement === null ?
      summaryElement
    : renderSummary({
        page,
        pageSize,
        pageCount,
        totalItems,
        rangeStart,
        rangeEnd,
        onPageSizeChange: changePageSize,
        defaultElement: summaryElement,
      });

  const renderNavButtonFor = (direction: PaginationNavDirection): ReactNode => {
    const isPrevious = direction === "previous";
    const onPress = isPrevious ? goToPreviousPage : goToNextPage;
    const isDisabled = disabled || !(isPrevious ? canGoPrevious : canGoNext);
    const defaultElement = (
      <PaginationNavButton
        direction={direction}
        onPress={onPress}
        disabled={isDisabled}
        accessibilityLabel={isPrevious ? previousLabel : nextLabel}
      />
    );

    if (renderNavButton === undefined) return defaultElement;
    return renderNavButton({ direction, onPress, disabled: isDisabled, defaultElement });
  };

  const renderPageButtonFor = (targetPage: number): ReactNode => {
    const selected = targetPage === page;
    const onPress = () => {
      goToPage(targetPage);
    };
    const defaultElement = (
      <PaginationPageButton
        page={targetPage}
        selected={selected}
        disabled={disabled}
        onPress={onPress}
        accessibilityLabel={formatPageLabel(targetPage)}
      />
    );

    if (renderPageButton === undefined) return defaultElement;
    return renderPageButton({ page: targetPage, selected, disabled, onPress, defaultElement });
  };

  return (
    <View style={rootStyle} testID={testID} role="navigation" accessibilityLabel={accessibilityLabel}>
      {/* Only mounted when there's a summary to show — an empty slot still
          counts as a flex child under `justifyContent: "space-between"`,
          which would shove `pageRow` to the far edge instead of leaving it
          as the sole item. */}
      {summary !== null && <View style={styles.summarySlot}>{summary}</View>}

      <View style={styles.pageRow}>
        {renderNavButtonFor("previous")}

        {items.map((item) =>
          item.type === "page" ?
            <View key={`page-${item.page.toString()}`}>{renderPageButtonFor(item.page)}</View>
            // Hidden from assistive technology: the gap is a layout artefact,
            // and the pages it stands for are still reachable by paging.
          : <View key={`ellipsis-${item.side}`} style={styles.ellipsis} aria-hidden accessibilityElementsHidden>
              <Text textStyle="sm/regular" color="textMuted">
                …
              </Text>
            </View>,
        )}

        {renderNavButtonFor("next")}
      </View>
    </View>
  );
};

Pagination.displayName = "Pagination";

export { Pagination };

function defaultFormatTotal(totalItems: number): string {
  return `of ${totalItems.toString()}`;
}

function defaultFormatPageLabel(page: number): string {
  return `Page ${page.toString()}`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  root: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    // Wraps rather than scrolls or truncates: on a narrow screen the summary
    // drops onto its own line and both halves stay usable.
    flexWrap: "wrap",
    gap: theme.spacing.xl,
    // The page-size menu is absolutely positioned outside this row.
    overflow: "visible",
  },
  rootBar: {
    marginHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    paddingHorizontal: theme.spacing["6xl"],
    backgroundColor: theme.colors.surface2.background,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
  // On a narrow screen `root` wraps this above `pageRow` rather than beside
  // it, so the two can visually overlap mid-transition. Without an explicit
  // stacking order here, `pageRow` — later in paint order — would cover the
  // page-size dropdown instead of sitting behind it.
  summarySlot: {
    zIndex: theme.zIndex.dropdown,
  },
  pageRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    zIndex: theme.zIndex.base,
  },
  ellipsis: {
    minWidth: CONTROL_SIZE,
    height: CONTROL_SIZE,
    alignItems: "center",
    justifyContent: "center",
  },
}));
